import {DecimalPipe} from '@angular/common'
import {Component, inject, OnInit, signal} from '@angular/core'
import {
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion'
import {getTakeUntilDestroyed} from '@sparbanken-syd/sparbanken-syd-theme'
import {debounceTime, filter, switchMap} from 'rxjs/operators'
import {KalpBuilding} from '../../model/kalp-building'
import {KalpLoan} from '../../model/kalp-loan'
import {DebtQuota, KalpService, Scenarios} from '../../services/kalp.service'

@Component({
  selector: 'spb-calculator',
  templateUrl: './calculator.component.html',
  styleUrls: ['./calculator.component.scss'],
  imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatExpansionPanelContent, DecimalPipe]
})
export class CalculatorComponent implements OnInit {
  public kalpService = inject(KalpService)

  /**
   * Used to hide additional data in case och scenario
   * omsättning/villkorsändring (Scenarios.Conditions)
   */
  public scenarioRenewal = signal(false)

  /**
   * If scenario is Häjning
   */
  public scenarioIncrease = signal(false)

  public amortizationAmount = signal(0)

  public building = signal<KalpBuilding>(new KalpBuilding())

  public loan = signal<KalpLoan>(new KalpLoan(0))

  public debtQuota = signal<DebtQuota>({} as any)

  public existingAmortization = signal(0)

  public additionalLoan = signal<KalpLoan>(new KalpLoan(0))

  public amortLabel = signal('Amorteringskrav')

  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  public ngOnInit(): void {
    this.kalpService.scenarioChange$.pipe(
      this.takeUntilDestroyed(),
      filter((s: Scenarios | null): s is Scenarios => s !== null),
      switchMap(scenario => {
        this.scenarioRenewal.set(scenario === Scenarios.Conditions)
        this.scenarioIncrease.set(scenario === Scenarios.Increase)
        return this.kalpService.buildings$
      }),
      switchMap(() => this.kalpService.kalpResult$),
      switchMap(() => this.kalpService.getActiveBuilding()),
      switchMap(building => {
        this.building.set(building)
        this.amortLabel.set(
          building.recommendedAmortization > 0
            ? 'Rekommenderad amortering'
            : 'Amorteringskrav'
        )

        return this.kalpService.loans$
      }),
      debounceTime(1000),
      switchMap(loans => {
        const currentBuilding = this.building()

        // All loans that are new and all loans connected to this property
        const newLoans = currentBuilding.loans
        const loanSum = newLoans.reduce((sum: number, loan: KalpLoan) => sum + loan.amount, 0)

        const tempLoan = new KalpLoan(currentBuilding.mortgageRequirementDebt)
        tempLoan.property = currentBuilding.id
        this.loan.set(tempLoan)

        // Conditions: existing loans are marked solve=true, other scenarios: non-solve existing loans
        this.existingAmortization.set(loans
          .filter((l: KalpLoan) => l.property === currentBuilding.id && !l.new && (this.scenarioRenewal() === l.solve))
          .reduce((sum: number, l: KalpLoan) => sum + l.mortgageAmount, 0))

        const onlyNewSum = loans
          .filter((l: KalpLoan) => l.new).reduce((sum: number, l: KalpLoan) => sum + l.amount, 0)

        const tempAdditionalLoan = new KalpLoan(loanSum)
        tempAdditionalLoan.mortgageAmount = onlyNewSum / 120
        this.additionalLoan.set(tempAdditionalLoan)

        return this.kalpService.getDebtQuota()
      })
    ).subscribe(dql => {
      this.debtQuota.set(dql)

      // We recreate the loan instance to ensure the signal detects the change and updates the template
      const currentBuilding = this.building()
      const updatedLoan = new KalpLoan(currentBuilding.mortgageRequirementDebt)
      updatedLoan.property = currentBuilding.id
      updatedLoan.setMortgagePercent(dql.amortizationRequirement)

      this.loan.set(updatedLoan)
      this.amortizationAmount.set(this.loan().mortgageAmount)
    })
  }
}